import React from "react"
import {
  academicYearStart,
  activityBand,
  clockTime,
  groupActivities,
  longDate,
  sinceDays,
} from "../learnerReportModel"
import { EmptyNote, ExpandButton, PillGroup, SUBJECT_FILTERS, SubjectChip } from "../reportUi"
import type { SubjectFilter } from "../reportUi"
import type { ReportView } from "./reportView"

type Period = "30d" | "3m" | "year"

const PERIODS: Array<{ key: Period; label: string }> = [
  { key: "30d", label: "Last 30 days" },
  { key: "3m", label: "Last 3 months" },
  { key: "year", label: "This academic year" },
]

const HistoryTab: React.FC<{ view: ReportView }> = ({ view }) => {
  const [subject, setSubject] = React.useState<SubjectFilter>("both")
  const [type, setType] = React.useState<string>("all")
  const [period, setPeriod] = React.useState<Period>("30d")
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState<string>("")

  const typeOptions = React.useMemo(
    () => Array.from(new Set(view.report.attempts.map(a => a.type_label))).sort(),
    [view.report.attempts]
  )

  const groups = React.useMemo(() => {
    const since =
      period === "30d"
        ? sinceDays(view.now, 30)
        : period === "3m"
        ? sinceDays(view.now, 91)
        : academicYearStart(view.now, view.country)
    const q = search.trim().toLowerCase()
    const attempts = view.report.attempts.filter(a => {
      const at = new Date(a.attempted_on)
      if (Number.isNaN(at.getTime()) || at < since) return false
      if (subject !== "both" && a.subject !== subject) return false
      if (type !== "all" && a.type_label !== type) return false
      if (q && !a.title.toLowerCase().includes(q) && !a.source_id.toLowerCase().includes(q)) return false
      return true
    })
    return groupActivities(attempts, view.nextTitle)
  }, [view, subject, type, period, search])

  const attemptCount = groups.reduce((t, g) => t + g.tries.length, 0)
  const bests = groups.map(g => g.best).filter((b): b is number => b !== null)
  const avgBest = bests.length ? (bests.reduce((t, b) => t + b, 0) / bests.length).toFixed(1) : "–"
  const repeats = groups.filter(g => g.tries.length > 1).length

  return (
    <>
      <section aria-label="Filters" className="sr-card sr-filters">
        <div className="sr-field">
          <span className="sr-field-label">Subject</span>
          <PillGroup options={SUBJECT_FILTERS} value={subject} onChange={setSubject} label="Subject" />
        </div>
        <label className="sr-field">
          <span className="sr-field-label">Activity type</span>
          <select className="sr-select" style={{ minWidth: 170 }} value={type} onChange={e => setType(e.target.value)}>
            <option value="all">All types</option>
            {typeOptions.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="sr-field">
          <span className="sr-field-label">Period</span>
          <select className="sr-select" style={{ minWidth: 150 }} value={period} onChange={e => setPeriod(e.target.value as Period)}>
            {PERIODS.map(p => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </label>
        <label className="sr-field" style={{ flex: "1 1 220px" }}>
          <span className="sr-field-label">Search</span>
          <input
            type="search"
            className="sr-input"
            placeholder="Content name or ID"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </label>
      </section>

      <section aria-label="Summary" className="sr-kpis sr-kpis-200">
        <div className="sr-card sr-kpi"><span className="sr-kpi-label">Activities</span><span className="sr-kpi-value sr-kpi-26">{groups.length}</span></div>
        <div className="sr-card sr-kpi"><span className="sr-kpi-label">Total attempts</span><span className="sr-kpi-value sr-kpi-26">{attemptCount}</span></div>
        <div className="sr-card sr-kpi"><span className="sr-kpi-label">Average best score</span><span className="sr-kpi-value sr-kpi-26">{avgBest}</span></div>
        <div className="sr-card sr-kpi"><span className="sr-kpi-label">Repeated attempts</span><span className="sr-kpi-value sr-kpi-26" style={{ color: "#B42335" }}>{repeats}</span></div>
      </section>

      <section aria-label="Activity log" className="sr-card sr-log">
        <div className="sr-log-head">
          <h2 className="sr-h2">Activity log</h2>
          <span className="sr-muted">Repeat attempts are grouped · open a row to see each try</span>
        </div>
        {groups.length === 0 ? (
          <div style={{ padding: "0 20px 20px" }}>
            <EmptyNote>No activities match these filters.</EmptyNote>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 980 }}>
              <div className="sr-log-grid sr-log-header">
                <span>Date</span><span>Subject</span><span>Content</span><span>Skill strand</span><span>Attempts</span><span>Best</span><span>Status</span><span></span>
              </div>
              {groups.map(g => {
                const band = activityBand(g.best)
                const multi = g.tries.length > 1
                const isOpen = multi && open === g.key
                const strandLine = g.strand && g.skill && g.strand !== g.skill ? `${g.strand} · ${g.skill}` : g.skill || "—"
                return (
                  <div key={g.key} className="sr-log-item">
                    <div className="sr-log-grid sr-log-row">
                      <span className="sr-log-date">{longDate(g.last)}</span>
                      <span style={{ justifySelf: "start" }}><SubjectChip subject={g.subject} small /></span>
                      <span className="sr-stack-2">
                        <span style={{ fontWeight: 600 }}>{g.title}</span>
                        <span className="sr-log-id">{g.typeLabel} · {g.sourceId}</span>
                      </span>
                      <span className="sr-log-strand">{strandLine}</span>
                      <span style={multi ? { fontWeight: 700, color: "#B42335" } : { color: "#4A5163" }}>{g.tries.length}</span>
                      <span className="sr-mono" style={{ fontWeight: 700, color: band.fg }}>{g.best === null ? "–" : g.best.toFixed(1)}</span>
                      <span
                        className="sr-status-chip"
                        style={multi ? { background: "#FDE6E9", color: "#A8243A" } : { background: band.bg, color: band.fg }}
                      >
                        {multi ? "Repeated" : band.label}
                      </span>
                      {multi ? (
                        <ExpandButton open={isOpen} onClick={() => setOpen(isOpen ? "" : g.key)} label="Show each attempt" />
                      ) : (
                        <span />
                      )}
                    </div>
                    {isOpen ? (
                      <div className="sr-tries-box">
                        <span className="sr-field-label">Each attempt</span>
                        <div className="sr-tries">
                          {g.tries.map((t, i) => (
                            <span key={i} className="sr-try">
                              Try {i + 1} at {clockTime(t.at)} · <b>{t.score === null ? "–" : t.score.toFixed(1)}</b>
                            </span>
                          ))}
                        </div>
                        {g.note ? <span className="sr-text-sub">{g.note}</span> : null}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </section>
    </>
  )
}

export default HistoryTab
